import { Delete, Edit, RemoveCircle } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { AuthorizedLink } from '~/components/links';
import { formatDateOnly } from '~/lib/datetime';
import { getScheduleDescription, isExpired } from '~/lib/schedules';
import type { DeviceSchedule__ScheduledShow as ScheduledShow } from '../../../../queries/schedule.generated';

export interface UseColumnsProps {
  currentScheduledShowId?: number;
  onDelete: (scheduledShow: ScheduledShow) => void;
  onRemoveDevice: (scheduledShow: ScheduledShow) => void;
  onUpdate: (scheduledShow: ScheduledShow) => void;
}

export const useColumns = ({
  currentScheduledShowId,
  onDelete,
  onRemoveDevice,
  onUpdate,
}: UseColumnsProps): GridColDef[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        field: 'name',
        flex: 0.15,
        headerName: t('common.labels.name'),
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => row.name,
      },
      {
        field: 'label',
        flex: 0.125,
        ...hiddenHeader(t('common.labels.status')),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {row.schedule?.allDay && (
                <Chip color="primary" label={t('pages.devices.show.schedule.all_day')} />
              )}
              {row.id === currentScheduledShowId && (
                <Chip color="success" label={t('pages.devices.show.schedule.active')} />
              )}
              {isExpired(row.schedule?.rrule) && (
                <Chip color="error" label={t('pages.devices.show.schedule.expired')} />
              )}
            </Box>
          );
        },
      },
      {
        field: 'show',
        flex: 0.15,
        headerName: t('pages.devices.show.schedule.columns.show'),
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          return (
            <AuthorizedLink
              authorized={row.show.canUpdate.value && !row.show.originId}
              entity={row.show}
            />
          );
        },
        sortable: false,
      },
      {
        field: 'start',
        headerName: t('pages.devices.show.schedule.columns.start'),
        minWidth: 150,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          return row.schedule?.startDate
            ? formatDateOnly(row.schedule.startDate, { zone: 'UTC' })
            : '';
        },
        sortable: false,
      },
      {
        field: 'schedule',
        headerName: t('pages.devices.show.schedule.columns.schedule'),
        sortable: false,
        flex: 0.25,
        renderCell: ({
          value: schedule,
        }: GridRenderCellParams<ScheduledShow, ScheduledShow['schedule']>) => {
          return schedule ? getScheduleDescription(schedule, t) : '';
        },
      },
      {
        field: 'devices',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          return <Chip color="primary" label={row.deviceCount || 0} />;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          if (!row.canUpdate.value) return [];

          const actions = [];

          actions.push(
            <ActionButton
              key={`schedule-edit-${row.id}`}
              title={t('pages.devices.show.schedule.edit_schedule')}
              Icon={Edit}
              onClick={() => onUpdate(row)}
            />,
          );

          actions.push(
            row.deviceCount === 1 ? (
              <ActionButton
                color="error"
                title={t('pages.devices.show.schedule.delete_schedule')}
                Icon={Delete}
                onClick={() => onDelete(row)}
              />
            ) : (
              <ActionButton
                color="error"
                title={t('common.dialogs.remove_device')}
                Icon={RemoveCircle}
                onClick={() => onRemoveDevice(row)}
              />
            ),
          );
          return actions;
        },
      },
    ],
    [currentScheduledShowId, isSmallAndUp, onDelete, onRemoveDevice, onUpdate, t],
  );
};
